import { cloneDeep } from 'lodash';
import ReactDOM from 'react-dom';
import DesignerSetting from '../../../DesignerSetting';
import { CheckboxGroup } from '../../../form_components';
import {
  hasLocalChanges,
  Route,
  navigateToRoute
} from '../../../utilities';
import BaseDialog from '../BaseDialog';
import { MergeDialog } from '../MergeDialog';
import './CheckDesignDialog.scss';
import { CheckDuplicateIds } from './CheckDuplicateIds';
import { fixDuplicateIdItems } from './fix-duplicate-id-items';

const isCollab = DesignerSetting.s.is_collab === "true";

/** Dialog used to validate various parts of the design. */
export class CheckDesignDialog extends BaseDialog {
  static _precheckOptions = [
    'Your team has saved all of their local changes.',
    'You have saved all of your local changes.',
    'All branches are merged.',
    'Your team knows that prior versions will no longer be allowed to save.'
  ].map((label, index) => ({
    label,
    value: index
  }));

  get _appManager() {
    return window.apm;
  }

  /**
   * Dialog for checking for issues in the design.
   * @module CheckDesignDialog
   * @param {Object} props React props.
   */
  constructor(props) {
    super(props);
    this._onLatestSerializedChange = this._onLatestSerializedChange.bind(this);
    this._onLatestVersionChange = this._onLatestVersionChange.bind(this);
    this._setFix = this._setFix.bind(this);

    const { latestVersion, serialized } = this._appManager;
    this.state = {
      completePrecheckOptions: new Set(),
      designChanged: this._checkForDesignChanges(),
      fixes: props.fixes ?? new Map([
        [fixDuplicateIdItems, this._getFixMetadata(fixDuplicateIdItems)]
      ]),
      isVersionOutdated: serialized.version !== latestVersion
    };
  }

  /**
   * Renders dialog to check the design.
   */
  static open() {
    const dialogId = 'check-design-dialog';
    const buttonsSelector = '#w2ui-popup .w2ui-popup-buttons';
    const appManager = window.apm;

    appManager.popup(
      [
        `<div>`,
        `   <span style="padding-right: 1em">Check Design</span>`,
        `</div>`
      ].join(''),
      `<div id="${dialogId}"></div>`,
      [
        `<button class="w2ui-btn secondary cancel-button"> ${window.l.popup.cancel} </button>`,
        `<button class="w2ui-btn primary save-button" disabled> Resolve Issues </button>`,
      ].join(''),
      [
        `#${dialogId}`,
        () => {
          const cancelButton = document.querySelector(`${buttonsSelector} .cancel-button`);
          const dialogContainer = document.getElementById(dialogId);
          const saveButton = document.querySelector(`${buttonsSelector} .save-button`);
          let selectedFixes = new Map();

          const closeDialog = () => {
            ReactDOM.unmountComponentAtNode(dialogContainer);
            w2popup.close();
          };

          ReactDOM.render(
            <CheckDesignDialog
              onSelectedFixesChanged={(fixes) => {
                selectedFixes = fixes;
                if (fixes.size) {
                  saveButton.removeAttribute('disabled');
                } else {
                  saveButton.setAttribute('disabled', true);
                }
              }}
            />,
            dialogContainer
          );

          cancelButton.addEventListener('click', closeDialog);

          saveButton.addEventListener('click', () => {
            const serialized = cloneDeep(appManager.serialized);
            selectedFixes.forEach((data, fix) => {
              fix({ serialized, ...data });
            });
            appManager.onValidateApplication();
            closeDialog();

            const commitMessages = [...selectedFixes.values()].map((fixData) => {
              return typeof fixData === 'object' ? fixData.commitMessage : null
            }).filter((message) => {
              return !!message;
            }).join(', ');

            navigateToRoute(Route.save);
            if(DesignerSetting.s.is_collab === "true"){
              MergeDialog.open({
                defaultCommitMessage: (commitMessages) ? `Fix ${commitMessages}.` : '',
                sourceDesign: serialized,
                sourceOriginDesign: appManager.latestSerialized,
                targetChangeMap: {},
                targetDesign: appManager.latestSerialized
              });
            } else {
              appManager.serialized = serialized;
              window.apm.setSerializedChanged();
            }
          });
        }
      ],
      700, // height
      null, // node
      null, // treenode
      false // showClose
    );
  }

  /** Run logic when component is added to the DOM. */
  componentDidMount() {
    super.componentDidMount();
    this._appManager.latestSerializedSubject.subscribe(this._onLatestSerializedChange);
    this._appManager.latestVersionSubject.subscribe(this._onLatestVersionChange);
  }

  /** Run logic when component is removed from the DOM. */
  componentWillUnmount() {
    super.componentWillUnmount();
    this._appManager.latestSerializedSubject.unsubscribe(this._onLatestSerializedChange);
    this._appManager.latestVersionSubject.unsubscribe(this._onLatestVersionChange);
  }

  /** Render the component. */
  render() {
    const {
      completePrecheckOptions,
      designChanged,
      fixes,
      isVersionOutdated
    } = this.state;
    const wrapContent = (content) => {
      const classes = [
        'container-fluid',
        'element-dialog',
        'check-design-dialog'
      ];

      if (typeof content === 'string') {
        classes.push('flex-center-column');
      }

      return (
        <div className={classes.join(' ')}>
          {content}
        </div>
      );
    };

    if (isVersionOutdated && isCollab) {
      return wrapContent(
        'Please download the latest version of the application prior to fixing design issues.'
      );
    } else if (designChanged == null) {
      return wrapContent('Loading...');
    } else if (designChanged) {
      return wrapContent(
        `${window.l.notification.unsaved_changes} fixing design issues.`
      );
    }

    const {
      designValidation,
      serialized
    } = this._appManager;
    const { duplicateIdItems } = designValidation ?? {};

    return wrapContent(
      <>
        <div className="panel-group">
          <div className="panel panel-default">
            <div className="panel-heading">
              <label>Duplicate IDs</label>
            </div>
            <div className="panel-body">
              <CheckDuplicateIds
                duplicateIdItems={duplicateIdItems}
                onShouldFixChange={(shouldFix) => {
                  this._setFix(shouldFix, fixDuplicateIdItems);
                }}
                serialized={serialized}
                shouldFix={fixes.has(fixDuplicateIdItems)}
              />
            </div>
          </div>
        </div>
        <CheckboxGroup
          label='Confirm the following prior to resolving issues.'
          name='check-design-precheck'
          onValueChange={(value, checked) => {
            const newOptions = new Set(completePrecheckOptions);
            if (checked) {
              newOptions.add(value);
            } else {
              newOptions.delete(value);
            }
            this.setState({
              completePrecheckOptions: newOptions
            });
            this._emitFixes({
              ...this.state,
              completePrecheckOptions: newOptions
            });
          }}
          options={CheckDesignDialog._precheckOptions}
          value={completePrecheckOptions}
        />
      </>
    );
  }

  /** Check for local changes by comparing against the latest version. */
  _checkForDesignChanges() {
    const { latestSerialized, serialized } = this._appManager;
    if (!latestSerialized) {
      return null;
    }

    if(DesignerSetting.s.is_collab === "false"){
      return false
    }

    return hasLocalChanges({
      design: serialized,
      latestDesign: latestSerialized
    });
  }

  /** Emit the fixes that should be resolved. */
  _emitFixes({
    completePrecheckOptions,
    fixes,
    isVersionOutdated
  }) {
    const allOptionsChecked = completePrecheckOptions.size === CheckDesignDialog._precheckOptions.length;
    const isValid = (!isVersionOutdated || !isCollab) && allOptionsChecked;
    this.props.onSelectedFixesChanged(isValid ? fixes : new Map());
  }

  /** Get metadata for the provided fix. */
  _getFixMetadata(fix) {
    const {
      designValidation,
      timestamp
    } = this._appManager;
    const { duplicateIdItems } = designValidation ?? {};

    switch (fix) {
      case fixDuplicateIdItems: {
        return {
          duplicateIdItems,
          commitMessage: 'duplicate ID items',
          timestamp
        };
      }
    }

    return null;
  }

  /** Handle the latest serialized version of the application changing. */
  _onLatestSerializedChange() {
    this.setState({
      designChanged: this._checkForDesignChanges()
    });
  }

  /** Handle the latest version of the application changing. */
  _onLatestVersionChange() {
    const { latestVersion, serialized } = this._appManager;
    const isVersionOutdated = serialized.version !== latestVersion;
    this.setState({ isVersionOutdated });
    this._emitFixes({
      ...this.state,
      isVersionOutdated
    });
  }

  /** Add/remove a fix to/from the list to resolve. */
  _setFix(shouldFix, fix) {
    let { fixes } = this.state;
    const newFixes = (
      (shouldFix)
      ? [...fixes, [fix, this._getFixMetadata(fix)]]
      : [...fixes].filter(([f]) => {
        return f !== fix;
      })
    );
    fixes = new Map(newFixes);
    this.setState({ fixes });
    this._emitFixes({
      ...this.state,
      fixes
    });
  }
}
